/* eslint-disable no-param-reassign */
import { useCallback, useContext } from 'react';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { GPC, PublicCompanyAdjustmentStatus } from 'api';
import { MEAN, MEDIAN, PERCENTILE, Q25, Q75 } from 'common/formulas/math.js';
import { ValuationsApproach } from 'common/types/valuation';
import {
  DEFAULT_MARKET_ADJUSTMENT_DECIMAL_PLACES,
  METRICS_KEYS_MAP,
} from 'pages/Valuations/approaches/backsolveApproach/constants';
import { parseGpcComparison } from 'pages/Valuations/approaches/guidelinePublicCompanies/gpc/config/utilities';
import { GPCAttributes } from 'pages/Valuations/approaches/guidelinePublicCompanies/utils/types';
import { useCombinedGPCComparisonPerfMetricAttrs } from 'pages/Valuations/approaches/guidelinePublicCompanies/utils/useCombinedGPCComparisonPerfMetricAttrs';
import { MARKET_DATE_NOT_SET } from 'pages/Valuations/util/constants';
import { BacksolveAttributes, GuidelinePublicCompaniesAttributes, PublicComp } from 'pages/ValuationsAllocation/types';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { Logger, MOMENT_DEFAULT_DATE_FORMAT } from 'utilities';

const PERCENTILE_SELECTION_A = 'Selection A';
const PERCENTILE_SELECTION_B = 'Selection B';
const SPECIFIED = 'Specified';

const ADJUSTMENTS_MAP: Record<string, (...args: any[]) => number> = {
  Median: MEDIAN,
  Mean: MEAN,
  '75th Percentile': Q75,
  '25th Percentile': Q25,
  'Selection A': PERCENTILE,
  'Selection B': PERCENTILE,
};

interface UseRefreshHelpersParams {
  measurementDate: any;
  backsolveAttributes?: BacksolveAttributes | null;
  gpcAttributes?: GuidelinePublicCompaniesAttributes | null;
}

export const useRefreshHelpers = ({ measurementDate, backsolveAttributes, gpcAttributes }: UseRefreshHelpersParams) => {
  const combinedGPCComparisonPerfMetricAttrs = useCombinedGPCComparisonPerfMetricAttrs(gpcAttributes);

  const getFlattenedComparisons = useCallback(
    (comparisons: PublicComp[]): GPC[] =>
      comparisons.map(
        gpc =>
          parseGpcComparison({
            data: gpc,
            attributes: combinedGPCComparisonPerfMetricAttrs as GPCAttributes,
          }) as GPC
      ),
    [combinedGPCComparisonPerfMetricAttrs]
  );

  type UpdateGPCApproachParams = {
    flattenedComparisons: GPC[];
    approach: ValuationsApproach;
  };

  const updateGPCApproach = useCallback(
    ({ flattenedComparisons, approach }: UpdateGPCApproachParams): ValuationsApproach => {
      const updatedComparisons: GPC[] = [];
      approach?.valuations_approach_gpc?.gpc_comparison?.forEach(element => {
        const updatedCom = flattenedComparisons?.find(comp => comp.cap_iq_id === element.cap_iq_id);
        if (updatedCom) {
          // Preserve *_enabled fields from database when merging with API data.
          // This is necessary to avoid losing the enabled fields when refreshing
          // the approach or getting the market data to see the last pricing date.
          const preservedEnabledFields = {
            first_metric_enabled: element.first_metric_enabled,
            second_metric_enabled: element.second_metric_enabled,
            first_forward_metric_enabled: element.first_forward_metric_enabled,
            second_forward_metric_enabled: element.second_forward_metric_enabled,
          };
          updatedComparisons.push({ ...element, ...updatedCom, ...preservedEnabledFields });
        } else {
          updatedComparisons.push({ ...element });
        }
      });
      return {
        ...approach,
        valuations_approach_gpc: {
          ...approach.valuations_approach_gpc,
          last_refresh_date: new Date().toISOString(),
          gpc_comparison: updatedComparisons,
        },
      } as ValuationsApproach;
    },
    []
  );

  const calculateMarketAdjustmentForBSApproach = useCallback(
    (
      approachBS: ValuationsApproach,
      approachGPC: ValuationsApproach,
      decimalPlaces: number = DEFAULT_MARKET_ADJUSTMENT_DECIMAL_PLACES
    ): number | null => {
      const adjustmentSelected = approachBS.valuations_approach_backsolve?.adjustment_selected;
      if ([null, undefined, ''].includes(adjustmentSelected)) {
        return null;
      }
      const metricKey =
        METRICS_KEYS_MAP[approachBS.valuations_approach_backsolve?.metric as keyof typeof METRICS_KEYS_MAP];

      const percentileSelection =
        adjustmentSelected === PERCENTILE_SELECTION_A
          ? approachBS.valuations_approach_backsolve?.percentile_selection_a
          : approachBS.valuations_approach_backsolve?.percentile_selection_b;

      if (adjustmentSelected !== SPECIFIED) {
        const rawData = approachBS.valuations_approach_backsolve?.public_companies_status
          ?.filter(pcs => pcs.enabled) // not sure if that is a good thing, we should probably do it for all of them
          .map(pcs => {
            const comparison = approachGPC.valuations_approach_gpc?.gpc_comparison?.find(
              comp => comp.cap_iq_id === pcs.cap_iq_id
            );
            const asOfDateMetric = Number(pcs[metricKey as keyof typeof pcs]);
            let percentageChange = 0.0;
            if (!comparison) {
              return percentageChange;
            }
            percentageChange =
              (Number(comparison[metricKey as keyof typeof comparison]) - asOfDateMetric) / asOfDateMetric;

            // Return null for invalid calculations (NaN, Infinity, -Infinity)
            if (!isFinite(percentageChange)) {
              return null;
            }

            return Number(percentageChange.toFixed(decimalPlaces));
          });

        const hasInvalid = rawData?.some(value => value === null);
        const data = rawData?.filter((value): value is number => value !== null);

        // Return null if data array is empty or contains invalid calculations
        if (!data?.length || hasInvalid) {
          return null;
        }

        if ([PERCENTILE_SELECTION_A, PERCENTILE_SELECTION_B].includes(adjustmentSelected as string)) {
          return ADJUSTMENTS_MAP[adjustmentSelected as string](data, percentileSelection);
        }
        return ADJUSTMENTS_MAP[adjustmentSelected as string](data);
      }
      return Number(approachBS.valuations_approach_backsolve?.specified_adjustment ?? '0.00');
    },
    []
  );

  const searchRelatedBSApproachesWithMarketAdjustment = useCallback(
    (approachGPC: ValuationsApproach, approaches: ValuationsApproach[]) => {
      const relatedBacksolveApproaches = approaches.filter(
        approach =>
          approach?.valuations_approach_backsolve?.adjust_for_market &&
          Number(approach?.valuations_approach_backsolve?.gpc_approach) === approachGPC.valuations_approach_gpc?.id
      );
      return relatedBacksolveApproaches;
    },
    []
  );

  const refreshBacksolveApproachWithMarketAdjustment = useCallback(
    (
      backsolveApproach: ValuationsApproach,
      approachGPC: ValuationsApproach,
      flattenedComparisonsByDate: Record<string, GPC[]>
    ) => {
      const asOfDate = backsolveApproach.valuations_approach_backsolve?.as_of_date;
      const asOfDateIso = moment(asOfDate, MOMENT_DEFAULT_DATE_FORMAT).toISOString().split('T')[0];
      const comparisons = flattenedComparisonsByDate[asOfDateIso] ?? [];

      const refreshedPublicCompaniesStatus: PublicCompanyAdjustmentStatus[] = [];

      approachGPC.valuations_approach_gpc?.gpc_comparison?.forEach(comparison => {
        const { cap_iq_id, id, ticker_symbol: comp_ticker_symbol } = comparison;
        const updatedComparison = comparisons.find(comparison => comparison.cap_iq_id === cap_iq_id);
        const toUpdate = backsolveApproach.valuations_approach_backsolve?.public_companies_status?.find(
          pcs => pcs.cap_iq_id === cap_iq_id
        );
        if (updatedComparison) {
          const { ltm_revenue, ltm_ebitda, ntm_revenue, ntm_ebitda, market_cap, enterprise_value, ticker_symbol } =
            updatedComparison;
          if (toUpdate) {
            // update the already existing public companies status
            const updatedPublicCompanyStatus = {
              ...toUpdate,
              ltm_revenue,
              ltm_ebitda,
              ntm_revenue,
              ntm_ebitda,
              market_cap,
              enterprise_value,
              ticker_symbol,
            } as PublicCompanyAdjustmentStatus;
            refreshedPublicCompaniesStatus.push(updatedPublicCompanyStatus);
          } else {
            // add the new comparison to public companies status
            const newPublicCompanyStatus = {
              ltm_revenue,
              ltm_ebitda,
              ntm_revenue,
              ntm_ebitda,
              market_cap,
              enterprise_value,
              ticker_symbol,
              cap_iq_id,
              valuation_approach_backsolve: backsolveApproach.valuations_approach_backsolve?.id,
              public_company: id,
              enabled: true,
            } as PublicCompanyAdjustmentStatus;
            refreshedPublicCompaniesStatus.push(newPublicCompanyStatus);
          }
        } else {
          // add empty comparison to public companies status
          const newPublicCompanyStatus = {
            ltm_revenue: 0.0,
            ltm_ebitda: 0.0,
            ntm_revenue: 0.0,
            ntm_ebitda: 0.0,
            market_cap: 0.0,
            enterprise_value: 0.0,
            reference_for_backsolve: null,
            ticker_symbol: comp_ticker_symbol,
            cap_iq_id,
            valuation_approach_backsolve: backsolveApproach.valuations_approach_backsolve?.id,
            public_company: id,
            enabled: true,
          } as unknown as PublicCompanyAdjustmentStatus;
          refreshedPublicCompaniesStatus.push(newPublicCompanyStatus);
        }
      });

      // remove public companies status related to removed gpc comparisons from the related gpc approach
      const existingPublicCompaniesStatus = approachGPC.valuations_approach_gpc?.gpc_comparison?.map(
        ({ cap_iq_id }) => cap_iq_id
      );
      const deletedPublicCompanyStatusIds = backsolveApproach.valuations_approach_backsolve?.public_companies_status
        ?.filter(pcs => pcs.id && !existingPublicCompaniesStatus?.includes(pcs.cap_iq_id))
        ?.map(({ id }) => id);

      const refreshedBackSolveApproach = {
        ...backsolveApproach,
        valuations_approach_backsolve: {
          ...backsolveApproach.valuations_approach_backsolve,
          deleted_public_company_status_ids: deletedPublicCompanyStatusIds,
          public_companies_status: refreshedPublicCompaniesStatus,
        },
      } as ValuationsApproach;

      let marketAdjustment = calculateMarketAdjustmentForBSApproach(
        refreshedBackSolveApproach,
        approachGPC,
        backsolveAttributes?.backsolveApproachAttrs?.market_adjustment?.decimal_places as number
      );
      // ensure we have the correct number of decimal places
      marketAdjustment = marketAdjustment
        ? Number(
          marketAdjustment.toFixed(
              backsolveAttributes?.backsolveApproachAttrs?.market_adjustment?.decimal_places as number
          )
        )
        : null;
      if (refreshedBackSolveApproach.valuations_approach_backsolve) {
        refreshedBackSolveApproach.valuations_approach_backsolve.market_adjustment = marketAdjustment?.toString();
      }
      return refreshedBackSolveApproach;
    },
    [calculateMarketAdjustmentForBSApproach, backsolveAttributes]
  );

  const getTickerListsByDate = useCallback(
    (
      outdatedApproaches: ValuationsApproach[],
      approaches: ValuationsApproach[],
      date: string | null | undefined // date in ISO format to keep mapping consistent
    ) => {
      // Collect all tickers and group them by date so we can
      // distribute them into several, parallel Cap IQ API calls
      const measurementValue = date ?? measurementDate.date;
      const tickerListsByDate = outdatedApproaches.reduce((acc: any, approach) => {
        const approachIdentifiers = approach.valuations_approach_gpc?.gpc_comparison?.map(({ cap_iq_id }) => cap_iq_id);
        const relatedBacksolveApproaches = searchRelatedBSApproachesWithMarketAdjustment(approach, approaches);
        if (!acc[measurementValue]) {
          acc[measurementValue] = [];
        }
        acc[measurementValue].push(...(approachIdentifiers as string[]));

        if (approach.valuations_approach_gpc) {
          approach.valuations_approach_gpc.last_refresh_date = new Date().toISOString();
        }

        relatedBacksolveApproaches.forEach(backsolveApproach => {
          const asOfDate = backsolveApproach.valuations_approach_backsolve?.as_of_date;
          const asOfDateIso = moment(asOfDate, MOMENT_DEFAULT_DATE_FORMAT).toISOString().split('T')[0];
          if (!acc[asOfDateIso]) {
            acc[asOfDateIso] = [];
          }
          acc[asOfDateIso].push(...(approachIdentifiers as string[]));
        });
        return acc;
      }, {});
      return tickerListsByDate;
    },
    [searchRelatedBSApproachesWithMarketAdjustment, measurementDate]
  );

  /**
   * Updates PC approaches and BS dependent approaches with map of updated comparisons by date.
   * Eventually will shift to let the PC approaches update and update the BS approaches in the backend
   */
  const getRefreshedApproaches = useCallback(
    ({
      publicCompsDataByDate,
      outdatedApproaches,
      approaches,
      marketDate,
    }: {
      publicCompsDataByDate: Record<string, PublicComp[]>;
      outdatedApproaches: ValuationsApproach[] | null;
      approaches: ValuationsApproach[];
      marketDate?: string | null;
    }) => {
      if (!isEmpty(combinedGPCComparisonPerfMetricAttrs)) {
        const flattenedComparisonsByDate: Record<string, GPC[]> = {};
        Object.entries(publicCompsDataByDate).forEach(([date, comparisons]) => {
          flattenedComparisonsByDate[date] = getFlattenedComparisons(comparisons);
        });
        if (!marketDate) {
          Logger.error(MARKET_DATE_NOT_SET, {});
          return [...approaches];
        }
        const refreshedApproaches: Record<string, ValuationsApproach> = {};
        outdatedApproaches?.forEach(approach => {
          const flattenedComparisons = flattenedComparisonsByDate[marketDate];
          const refreshedApproach = updateGPCApproach({ flattenedComparisons, approach });

          const key = approach.id ?? approach.panelId;
          refreshedApproaches[key as string] = refreshedApproach;
          // search related backsolve approaches with adjust for market
          // to refresh them and add to refreshed Approaches dict
          const relatedBacksolveApproaches = searchRelatedBSApproachesWithMarketAdjustment(approach, approaches);
          const refreshedBacksolveApproaches = relatedBacksolveApproaches.map(approachBS =>
            refreshBacksolveApproachWithMarketAdjustment(approachBS, refreshedApproach, flattenedComparisonsByDate)
          );
          refreshedBacksolveApproaches.forEach(refreshedBacksolveApproach => {
            const approachKey = refreshedBacksolveApproach.id
              ? refreshedBacksolveApproach.id
              : refreshedBacksolveApproach.panelId;
            refreshedApproaches[approachKey as string] = refreshedBacksolveApproach;
          });
        });

        const updatedApproaches = approaches.map(approach => {
          const refreshedApproach = approach.id
            ? refreshedApproaches[approach.id]
            : refreshedApproaches[approach.panelId as string];
          if (refreshedApproach) {
            return refreshedApproach;
          }
          return approach;
        });

        return updatedApproaches;
      }

      return approaches;
    },
    [
      getFlattenedComparisons,
      refreshBacksolveApproachWithMarketAdjustment,
      searchRelatedBSApproachesWithMarketAdjustment,
      updateGPCApproach,
      combinedGPCComparisonPerfMetricAttrs,
    ]
  );

  return {
    getTickerListsByDate,
    getRefreshedApproaches,
    getFlattenedComparisons,
  };
};

export const useRefreshHelpersWithContext = () => {
  const { marketDate, backsolveAttributes, gpcAttributes } = useContext(ValuationContext);

  const refreshHelpers = useRefreshHelpers({
    measurementDate: marketDate,
    backsolveAttributes,
    gpcAttributes,
  });

  return refreshHelpers;
};
